import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Sparkles } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2P — Tonight's Biggest Moments.
 *
 * Velocity-ranked clips with cultural-moment boosts (rivalry / title /
 * comeback).  Engagement-bait is naturally filtered (zero-reaction clips
 * score 0).  Renders as a horizontally-scrollable strip atop the City Feed.
 *
 * Per CEO: "Trending should favor cultural moments, NOT engagement bait."
 */
const BADGE_META = {
  title_moment:    { label: 'TITLE MOMENT',    emoji: '🏆', tone: 'bg-amber-400 text-slate-950 ring-amber-300/50' },
  rivalry_classic: { label: 'RIVALRY CLASSIC', emoji: '⚔️', tone: 'bg-rose-500 text-slate-50 ring-rose-400/50' },
  crew_moment:     { label: 'CREW MOMENT',     emoji: '🔥', tone: 'bg-orange-500 text-slate-50 ring-orange-400/50' },
};

export default function ArenaxTrendingStrip({ city, sport }) {
  const [data, setData] = useState({ feed_header: null, clips: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    if (sport) params.set('sport', sport);
    params.set('limit', '8');
    api.get(`/arenax/trending?${params.toString()}`)
      .then(r => setData(r.data || { feed_header: null, clips: [] }))
      .catch(() => setData({ feed_header: null, clips: [] }))
      .finally(() => setLoading(false));
  }, [city, sport]);

  if (loading && data.clips.length === 0) return null;
  if (!data.clips || data.clips.length === 0) return null;

  const header = data.feed_header || {};
  return (
    <section className="rounded-2xl border border-purple-500/25 overflow-hidden"
      data-testid="arenax-trending-strip"
      style={{ background: 'linear-gradient(160deg, rgba(124,58,237,0.07), rgba(244,63,94,0.04) 50%, var(--ax-card-bg-soft))' }}>
      <div className="px-5 pt-5 pb-3 border-b border-purple-500/15">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="h-4 w-4 text-purple-300" />
          <Sparkles className="h-3 w-3 text-purple-300 animate-pulse" />
          <span className="text-[10px] tracking-[0.3em] text-purple-300 font-black uppercase">
            Trending
          </span>
        </div>
        <h2 className="text-xl sm:text-2xl font-black text-slate-100 leading-tight"
          data-testid="arenax-trending-strip-title">
          {header.title || "Tonight's biggest moments 👀"}
        </h2>
        {header.subtitle && (
          <p className="text-xs text-slate-400 mt-1"
            data-testid="arenax-trending-strip-subtitle">{header.subtitle}</p>
        )}
      </div>
      <div className="overflow-x-auto px-4 py-4">
        <div className="flex gap-3 min-w-min">
          {data.clips.map(clip => (
            <TrendingClipTile key={clip.id} clip={clip} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TrendingClipTile({ clip }) {
  const videoRef = useRef(null);
  const streamUrl = `${process.env.REACT_APP_BACKEND_URL}/api/arenax/clips/${clip.id}/stream`;
  const onEnter = () => { try { videoRef.current?.play(); } catch (_e) { /* no-op */ } };
  const onLeave = () => { try { videoRef.current?.pause(); videoRef.current.currentTime = 0; } catch (_e) { /* no-op */ } };
  const badge = clip.badge && BADGE_META[clip.badge];
  return (
    <Link to={`/arenax/matches/${clip.match_id}/spectate`}
      data-testid={`arenax-trending-clip-${clip.id}`}
      onMouseEnter={onEnter} onMouseLeave={onLeave}
      className="shrink-0 w-40 rounded-xl overflow-hidden border border-purple-500/30 ring-1 ring-purple-500/20 bg-slate-950 hover:translate-y-[-2px] transition-transform">
      <div className="relative aspect-[9/16] bg-slate-900">
        <video ref={videoRef} src={streamUrl} preload="metadata" muted playsInline loop
          className="absolute inset-0 w-full h-full object-cover" />
        {badge && (
          <div className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[8px] font-black uppercase
                            tracking-wider flex items-center gap-1 ring-2 ${badge.tone}`}
            data-testid={`arenax-trending-badge-${clip.badge}-${clip.id}`}>
            <span>{badge.emoji}</span>
            <span>{badge.label}</span>
          </div>
        )}
        {/* Velocity tag */}
        <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-slate-950/85 text-purple-300
                          text-[8px] font-mono"
          data-testid={`arenax-trending-velocity-${clip.id}`}>
          ⚡ {clip.velocity_score?.toFixed(1)}
        </div>
      </div>
      <div className="px-2.5 py-2">
        <p className="text-[10.5px] text-slate-200 leading-snug line-clamp-3"
          data-testid={`arenax-trending-caption-${clip.id}`}>
          {clip.caption_narrative}
        </p>
      </div>
    </Link>
  );
}
